import { Component, OnInit, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, UntypedFormBuilder, ValidationErrors } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService, UtilsService } from 'src/app/services';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CompanyProductDistributionTimeDialogComponent } from '../company-product-distribution-time-dialog/company-product-distribution-time-dialog.component';
import { CompanyProductDefaultTimeDistribution } from 'src/app/models/company_product_default_time_distribution.model';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDatepickerModule } from '@angular/material/datepicker';
import dayjs from 'dayjs';
import { MatError } from '@angular/material/form-field';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  DatePickerDialogComponent,
  DatePickerDialogData,
} from 'src/app/components/main/shared/date-picker-dialog/date-picker-dialog.component';
import { DayOfWeekEnum } from 'src/app/enums/day-of-week.enum';

@Component({
  selector: 'app-company-product-default-time-distribution-crud-dialog',
  imports: [
    MatIconModule,
    MatDialogModule,
    MatTableModule,
    MatButtonModule,
    MatInputModule,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatDatepickerModule,
    FormsModule,
    MatError,
  ],
  templateUrl: './company-product-default-time-distribution-crud-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './company-product-default-time-distribution-crud-dialog.component.css',
})
export class CompanyProductDefaultTimeDistributionCrudDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CompanyProductDefaultTimeDistributionCrudDialogComponent>>(MatDialogRef);
  protected utils = inject(UtilsService);
  private api = inject(ApiService);
  protected activatedRoute = inject(ActivatedRoute);
  protected fb = inject(UntypedFormBuilder);
  protected router = inject(Router);
  private dialog = inject(MatDialog);
  data = inject<{
    company_product_id: number;
  }>(MAT_DIALOG_DATA);

  form: FormGroup;
  displayedColumns: string[] = ['is_active', 'start_date', 'end_date', 'updated_at', 'actions'];

  dataSource: MatTableDataSource<CompanyProductDefaultTimeDistributionCustom>;
  readonly today: Date = new Date();
  readonly tomorrow: Date = dayjs().add(1, 'day').startOf('day').toDate();
  past_date_error_state_matcher = signal<ErrorStateMatcher>({
    isErrorState: (): boolean => !!this.form?.errors?.['pastDateWithSingleTimeDistribution'],
  });

  ngOnInit(): void {
    this.fetchRecords();
    this.initForm();
  }

  isMonday(date: Date | null): boolean {
    if (!date) return false;
    return dayjs(date).day() === 1;
  }

  isSunday(date: Date | null): boolean {
    if (!date) return false;
    return dayjs(date).day() === 0;
  }

  cloneRecord(record: CompanyProductDefaultTimeDistribution): void {
    const current_week_monday = dayjs().startOf('isoWeek').startOf('day');
    const min_from_source = dayjs(record.start_date).add(1, 'week').startOf('day');
    const min_date = (min_from_source.isAfter(current_week_monday) ? min_from_source : current_week_monday).toDate();
    const dialog_data: DatePickerDialogData = {
      dialog_title: '¿Cuándo debe empezar la nueva distribución?',
      dialog_ok_button: 'Clonar',
      label: 'Fecha de inicio (lunes)',
      selected_date: min_date,
      min_date,
      filter: DayOfWeekEnum.Monday,
    };

    this.dialog
      .open(DatePickerDialogComponent, { width: '400px', data: dialog_data })
      .afterClosed()
      .subscribe((new_start_date: Date | undefined): void => {
        if (!new_start_date) return;

        this.api
          .cloneCompanyProductDefaultTimeDistribution(
            this.data.company_product_id.toString(),
            record.id,
            dayjs(new_start_date).format('YYYY-MM-DD')
          )
          .subscribe((): void => {
            this.fetchRecords();
          });
      });
  }

  updateDates(record: CompanyProductDefaultTimeDistribution): void {
    if (record.start_date === null) {
      return;
    }

    this.form.patchValue({ end_date: dayjs(record.end_date).format('YYYY-MM-DD') });
    if (this.form.invalid) {
      return;
    }

    const start_date = record.start_date ? dayjs(record.start_date).format('YYYY-MM-DD') : null;
    const end_date = record.end_date ? dayjs(record.end_date).format('YYYY-MM-DD') : null;

    this.api
      .updateCompanyProductDefaultTimeDistribution(this.data.company_product_id.toString(), { start_date, end_date }, record.id)
      .subscribe(() => {
        this.fetchRecords();
      });
  }

  openViewRecordDialog(record: CompanyProductDefaultTimeDistribution): void {
    this.openEditRecordDialog(record, true);
  }

  openEditRecordDialog(record: CompanyProductDefaultTimeDistribution, is_view: boolean = false): void {
    this.dialog
      .open(CompanyProductDistributionTimeDialogComponent, {
        width: '700px',
        data: {
          company_product_id: this.data.company_product_id,
          companyProductDefaultTimeDistributionId: record.id,
          is_view,
        },
      })
      .afterClosed()
      .subscribe((data: CompanyProductDefaultTimeDistribution | undefined): void => {
        if (data === undefined) return;

        this.fetchRecords();
      });
  }

  openDeleteRecordDialog(record: CompanyProductDefaultTimeDistribution): void {
    if (!confirm('¿Seguro que quieres eliminar la distribución de tiempo?')) return;

    this.api.deleteCompanyProductDefaultTimeDistribution(this.data.company_product_id.toString(), record.id).subscribe(() => {
      this.fetchRecords();
    });
  }

  private getMinStartDate(record: CompanyProductDefaultTimeDistribution, data: CompanyProductDefaultTimeDistribution[]): Date {
    const currentIndex = data.findIndex(item => item.id === record.id);
    const previousCondition = data[currentIndex - 1];

    if (!previousCondition || !previousCondition.end_date) return this.tomorrow;

    return dayjs(previousCondition.end_date).add(1, 'day').startOf('day').toDate();
  }

  private getMaxStartDate(record: CompanyProductDefaultTimeDistribution): Date | null {
    return record.end_date ? dayjs(record.end_date).startOf('day').toDate() : null;
  }

  private getMinEndDate(record: CompanyProductDefaultTimeDistribution): Date {
    return dayjs(record.start_date).add(1, 'day').startOf('day').toDate();
  }

  private getMaxEndDate(record: CompanyProductDefaultTimeDistribution, data: CompanyProductDefaultTimeDistribution[]): Date | null {
    const currentIndex = data.findIndex(item => item.id === record.id);
    const nextCondition = data[currentIndex + 1];

    if (!nextCondition) return null;

    return dayjs(nextCondition.start_date).subtract(1, 'day').startOf('day').toDate();
  }

  private getEditable(record: CompanyProductDefaultTimeDistribution): boolean {
    return dayjs(record.start_date).isSameOrAfter(dayjs().startOf('week'), 'day');
  }

  private fetchRecords(): void {
    this.api.getCompanyProductDefaultTimeDistributions(this.data.company_product_id.toString()).subscribe((data): void => {
      const customData: CompanyProductDefaultTimeDistributionCustom[] = data.map((record): CompanyProductDefaultTimeDistributionCustom => {
        return {
          ...record,
          minStartDate: this.getMinStartDate(record, data),
          maxStartDate: this.getMaxStartDate(record),
          minEndDate: this.getMinEndDate(record),
          maxEndDate: this.getMaxEndDate(record, data),
          editable: this.getEditable(record),
        };
      });

      this.dataSource = new MatTableDataSource<CompanyProductDefaultTimeDistributionCustom>(customData);
    });
  }

  private initForm(): void {
    this.form = this.fb.group(
      {
        end_date: [null],
      },
      { validators: [this.pastDateWithSingleTimeDistributionValidator] }
    );
  }

  private pastDateWithSingleTimeDistributionValidator = (control: AbstractControl): ValidationErrors | null => {
    const end_date = control.get('end_date')?.value;
    if (end_date !== null && dayjs(end_date).isBefore(dayjs().startOf('day'), 'day') && this.dataSource?.data?.length === 1) {
      return { pastDateWithSingleTimeDistribution: true };
    }
    return null;
  };
}

interface CompanyProductDefaultTimeDistributionCustom extends CompanyProductDefaultTimeDistribution {
  minStartDate: Date;
  maxStartDate: Date | null;
  minEndDate: Date;
  maxEndDate: Date | null;
  editable: boolean;
}
